import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core'
import { FlexModule } from '@angular/flex-layout'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatSelectModule } from '@angular/material/select'
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar'
import { AppointmentAuthorizationService, PatientUploadedFile } from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, combineLatest, finalize, map, shareReplay, startWith, Subject, takeUntil, tap } from 'rxjs'

import { AuthAttachmentsTableComponent } from '../auth-attachments-table'
import { FileUploaderComponent } from '../file-uploader'

export interface PendingAuthorizationAttachment {
  file: File
  documentType: string
}

@Component({
  selector: 'mmx-auth-attachments',
  templateUrl: './auth-attachments.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    AuthAttachmentsTableComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSnackBarModule,
    MatButtonModule,
    MatIconModule,
    FileUploaderComponent,
    FlexModule,
    TranslateModule,
  ],
})
export class AuthAttachmentsComponent implements OnChanges, OnDestroy {
  @Input()
  public authorizationId: string

  @Input()
  public attachments: PatientUploadedFile[]

  @Input()
  public attachmentsLoading = false

  @Output()
  public newAttachmentsAdded = new EventEmitter<void>()

  @Output()
  public pendingAttachments = new EventEmitter<PendingAuthorizationAttachment[]>()

  public documentsUploading$ = new BehaviorSubject<boolean>(false)
  public formGroup = this.fb.group({})
  private onChanges$ = new Subject<SimpleChanges>()

  public documentTypes = [
    { value: 'referral', viewValue: 'patient-details.authorization-card.attachments.referral' },
    { value: 'order', viewValue: 'patient-details.authorization-card.attachments.order' },
    { value: 'clinical_notes', viewValue: 'patient-details.authorization-card.attachments.clinical_notes' },
    { value: 'office_notes', viewValue: 'patient-details.authorization-card.attachments.office_notes' },
    { value: 'previous_results', viewValue: 'patient-details.authorization-card.attachments.previous_results' },
    { value: 'clinical_history', viewValue: 'patient-details.authorization-card.attachments.clinical_history' },
    { value: 'other', viewValue: 'patient-details.authorization-card.attachments.other' },
  ]

  public files$ = this.formGroup.valueChanges.pipe(
    startWith(null),
    map(() => Object.keys(this.formGroup.value || {})
      .map((key) => ({
        key,
        value: this.formGroup.get(key).value,
      }))
      .filter(({ value }) => !!value?.file),
    ),
    tap((files) => {
      if (!files.length) {
        this.pendingAttachments.emit([])
        return
      }

      if (this.formGroup.invalid) {
        return
      }

      const value = files.map(({ value }) => value)
      this.pendingAttachments.emit(value)
    }),
    shareReplay(1),
  )

  public hasFiles$ = this.files$.pipe(
    map((files) => files.length > 0),
  )

  public fileUploaderLabel$ = this.hasFiles$.pipe(
    map((hasFiles) => hasFiles
      ? 'patient-details.authorization-card.attachments.add-more-files'
      : 'patient-details.authorization-card.attachments.add-files'),
  )

  public isUploadButtonDisabled$ = combineLatest([
    this.files$,
    this.documentsUploading$,
    this.formGroup.statusChanges.pipe(startWith(this.formGroup.status)),
    this.onChanges$.pipe(startWith({})),
  ]).pipe(
    map(([files, uploading]) => this.formGroup.invalid || !files.length || uploading || this.attachmentsLoading),
  )

  private onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
    private snackbar: MatSnackBar,
    private authorizationSvc: AppointmentAuthorizationService,
    private t: TranslateService,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    this.onChanges$.next(changes)
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onFileSelected(files: File[]) {
    for (const file of files) {
      this.formGroup.addControl(
        Math.random().toString(36).substring(2, 15),
        this.fb.group({
          file: this.fb.control(file, Validators.required),
          documentType: this.fb.control(null, Validators.required),
        }),
      )
    }
    this.formGroup.updateValueAndValidity()
  }

  public onUploadFiles() {
    if (this.formGroup.invalid) {
      return
    }

    this.documentsUploading$.next(true)

    return this.authorizationSvc.uploadAuthAttachments(this.authorizationId, Object.values(this.formGroup.value || {}))
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.documentsUploading$.next(false)),
      )
      .subscribe(() => {
        this.newAttachmentsAdded.emit()
        this.pendingAttachments.emit([])
        this.formGroup.reset()
        this.snackbar.open(this.t.instant('patient-details.authorization-card.attachments.upload-success'), null, { duration: 3000 })
      })
  }

  public onDeleteAttachments(files: PatientUploadedFile[]) {
    this.authorizationSvc.deleteAttachments(this.authorizationId, files.map((f) => f.key))
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.newAttachmentsAdded.emit()
        this.snackbar.open(this.t.instant('patient-details.authorization-card.attachments.delete-success'), null, { duration: 3000 })
      })
  }

  public getDocumentTypeViewValue(type: string) {
    return this.documentTypes.find((dt) => dt.value === type)?.viewValue
  }
}
